import React, { useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { ApiTestCase, TEST_CATEGORIES, TEST_SUITE, buildPerfectPayload } from './testSuite'

const BASE_URL =
    process.env.REACT_APP_API_URL == 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

interface DryRunResult {
    outcome: 'accepted' | 'rejected'
    gate: string
    http_status: number
    rejection_reason: string | null
    body: any
}

interface TestRun {
    state: 'idle' | 'running' | 'pass' | 'fail' | 'error'
    payload?: any
    result?: DryRunResult
    verdict?: string
}

const evaluateRun = (test: ApiTestCase, result: DryRunResult): { pass: boolean; verdict: string } => {
    const problems: string[] = []
    if (result.outcome !== test.expected.outcome) {
        problems.push(`expected outcome "${test.expected.outcome}" but got "${result.outcome}"`)
    }
    if (result.http_status !== test.expected.status) {
        problems.push(`expected HTTP ${test.expected.status} but got ${result.http_status}`)
    }
    if ((result.rejection_reason ?? null) !== test.expected.reasonCode) {
        problems.push(`expected reason "${test.expected.reasonCode ?? 'none'}" but got "${result.rejection_reason ?? 'none'}"`)
    }
    if (test.expected.detailMatch) {
        const haystack = JSON.stringify(result.body ?? {}).toLowerCase()
        if (!haystack.includes(test.expected.detailMatch.toLowerCase())) {
            problems.push(`response did not mention "${test.expected.detailMatch}"`)
        }
    }
    if (problems.length) return { pass: false, verdict: problems.join('; ') }
    return {
        pass: true,
        verdict:
            result.outcome === 'accepted'
                ? 'API accepted the payload, as expected.'
                : `API rejected at the ${result.gate.replace(/_/g, ' ')} gate with ${result.http_status} ${result.rejection_reason}, as expected.`,
    }
}

interface DefendantAliasRow {
    alias: string | null
    defendant: string
}

const csvCell = (value: string | null | undefined): string => {
    if (value == null) return ''
    return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value
}

const downloadDefendantAliasCsv = (rows: DefendantAliasRow[]) => {
    const lines = [
        'Defendant alias,Defendant (lender)',
        ...rows.map((row) => `${csvCell(row.alias)},${csvCell(row.defendant)}`),
    ]
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ffj-defendant-aliases-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
}

interface CfWebhookTest {
    webhookUrl: string | null
    payload: any
    sending: boolean
    sendResult: any
    sendError: string | null
}

export const BaseClaimApiTestView = () => {
    const [approvedLenders, setApprovedLenders] = useState<string[]>([])
    const [selectedLender, setSelectedLender] = useState<string>('')
    const [runs, setRuns] = useState<Record<string, TestRun>>({})
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [runningAll, setRunningAll] = useState(false)
    const [exportingAliases, setExportingAliases] = useState(false)
    const [cfTest, setCfTest] = useState<CfWebhookTest>({
        webhookUrl: null,
        payload: null,
        sending: false,
        sendResult: null,
        sendError: null,
    })

    useEffect(() => {
        const fetchLenders = async () => {
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-approved-lenders`, {})
                const lenders: string[] = response.data?.lenders ?? []
                setApprovedLenders(lenders)
                if (lenders.length) setSelectedLender(lenders[0])
            } catch (error: any) {
                Report.failure(
                    'Error',
                    error?.response?.data?.message || 'Could not load the approved lender list.',
                    'Okay'
                )
            }
        }
        const fetchCfPreview = async () => {
            try {
                const response = await api.post(`${BASE_URL}/api/ffj-cf-onboarding-preview`, {})
                setCfTest((current) => ({
                    ...current,
                    webhookUrl: response.data?.webhook_url ?? null,
                    payload: response.data?.payload ?? null,
                }))
            } catch {
                // The panel shows its own "could not load" state; the test
                // suite itself is unaffected.
            }
        }
        fetchLenders()
        fetchCfPreview()
    }, [])

    const exportDefendantAliases = async () => {
        setExportingAliases(true)
        try {
            const response = await api.post(`${BASE_URL}/api/ffj-defendant-aliases`, {})
            const rows: DefendantAliasRow[] = response.data?.rows ?? []
            if (!response.data?.success) throw new Error(response.data?.message || 'Export request failed')
            if (!rows.length) throw new Error('No defendants found in the funder-monitoring schema.')
            downloadDefendantAliasCsv(rows)
        } catch (error: any) {
            Report.failure(
                'Export failed',
                error?.response?.data?.message || error?.message || 'Could not export the defendant aliases.',
                'Okay'
            )
        } finally {
            setExportingAliases(false)
        }
    }

    const sendCfTestWebhook = async () => {
        setCfTest((current) => ({ ...current, sending: true, sendResult: null, sendError: null }))
        try {
            const response = await api.post(`${BASE_URL}/api/ffj-cf-onboarding-test-send`, {
                payload: cfTest.payload,
            })
            setCfTest((current) => ({
                ...current,
                sending: false,
                sendResult: response.data,
                sendError: response.data?.success ? null : response.data?.message ?? 'Send failed',
            }))
        } catch (error: any) {
            setCfTest((current) => ({
                ...current,
                sending: false,
                sendError: error?.response?.data?.message || error?.message || 'Send failed',
            }))
        }
    }

    const basePayload = useMemo(
        () => (selectedLender ? buildPerfectPayload(selectedLender) : null),
        [selectedLender]
    )

    const toggleExpanded = (id: string) => {
        setExpanded((current) => {
            const next = new Set(current)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const runTest = async (test: ApiTestCase): Promise<boolean> => {
        if (!basePayload) return false
        const payload = test.build(basePayload)
        setRuns((current) => ({ ...current, [test.id]: { state: 'running', payload } }))
        try {
            const response = await api.post(`${BASE_URL}/api/ffj-webhook-dry-run`, { payload })
            const result: DryRunResult = response.data?.result
            if (!response.data?.success || !result) throw new Error(response.data?.message || 'No result returned')
            const { pass, verdict } = evaluateRun(test, result)
            setRuns((current) => ({
                ...current,
                [test.id]: { state: pass ? 'pass' : 'fail', payload, result, verdict },
            }))
            return pass
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Request failed'
            setRuns((current) => ({
                ...current,
                [test.id]: { state: 'error', payload, verdict: `Dry-run request failed: ${message}` },
            }))
            return false
        }
    }

    const runAll = async () => {
        setRunningAll(true)
        for (const test of TEST_SUITE) {
            // Sequential on purpose: deterministic ordering and no hammering.
            // eslint-disable-next-line no-await-in-loop
            await runTest(test)
        }
        setRunningAll(false)
    }

    const summary = useMemo(() => {
        const states = TEST_SUITE.map((t) => runs[t.id]?.state ?? 'idle')
        return {
            total: TEST_SUITE.length,
            pass: states.filter((s) => s === 'pass').length,
            fail: states.filter((s) => s === 'fail').length,
            error: states.filter((s) => s === 'error').length,
            ran: states.filter((s) => s !== 'idle' && s !== 'running').length,
        }
    }, [runs])

    const stateChip = (run?: TestRun) => {
        const state = run?.state ?? 'idle'
        const labels: Record<string, string> = {
            idle: 'Not run',
            running: 'Running…',
            pass: 'PASS',
            fail: 'FAIL',
            error: 'ERROR',
        }
        return <span className={`${styles.stateChip} ${styles[`state_${state}`]}`}>{labels[state]}</span>
    }

    return (
        <div className={styles.testSuite}>
            <div className={styles.controls}>
                <div className={styles.controlGroup}>
                    <label>Approved lender for baseline payloads</label>
                    <select
                        value={selectedLender}
                        onChange={(e) => setSelectedLender(e.target.value)}
                        disabled={!approvedLenders.length || runningAll}
                    >
                        {!approvedLenders.length && <option value="">Loading lenders…</option>}
                        {approvedLenders.map((lender) => (
                            <option key={lender} value={lender}>
                                {lender}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.controlActions}>
                    <div className={styles.summary}>
                        <span>
                            {summary.ran}/{summary.total} run
                        </span>
                        <span className={styles.summaryPass}>{summary.pass} pass</span>
                        <span className={styles.summaryFail}>{summary.fail} fail</span>
                        {summary.error > 0 && <span className={styles.summaryError}>{summary.error} error</span>}
                    </div>
                    <button
                        className={styles.exportAliasesButton}
                        onClick={exportDefendantAliases}
                        disabled={exportingAliases}
                        title="Download every defendant alias from the funder-monitoring defendants table, with the canonical defendant (lender) each alias resolves to."
                    >
                        {exportingAliases ? 'Exporting…' : 'Export Defendant Aliases CSV'}
                    </button>
                    <button className={styles.runAllButton} onClick={runAll} disabled={!basePayload || runningAll}>
                        {runningAll ? 'Running suite…' : 'Run All Tests'}
                    </button>
                </div>
            </div>

            <p className={styles.note}>
                Every test is evaluated through the live validation pipeline in <strong>dry-run mode</strong> — the
                exact same gates, in the exact same order, as <code>POST /ffj/webhook</code>, but nothing is
                recorded or written anywhere. A test <em>passes</em> when the API responds exactly as the case
                expects (including the rejection reason and the failing item appearing in the response).
            </p>

            {TEST_CATEGORIES.map((category) => {
                const tests = TEST_SUITE.filter((t) => t.category === category)
                return (
                    <div key={category} className={styles.categoryBlock}>
                        <h4 className={styles.categoryTitle}>
                            {category} <span className={styles.categoryCount}>{tests.length} tests</span>
                        </h4>
                        {tests.map((test) => {
                            const run = runs[test.id]
                            const isOpen = expanded.has(test.id)
                            const previewPayload = run?.payload ?? (basePayload ? test.build(basePayload) : null)
                            return (
                                <div key={test.id} className={styles.testRow}>
                                    <div className={styles.testHeader} onClick={() => toggleExpanded(test.id)}>
                                        <span className={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
                                        <div className={styles.testTitle}>
                                            <span className={styles.testName}>{test.name}</span>
                                            <span className={styles.failingItem}>{test.failingItem}</span>
                                        </div>
                                        <span className={styles.expectedChip}>
                                            {test.expected.outcome === 'accepted'
                                                ? 'expect 201 accepted'
                                                : `expect ${test.expected.status} ${test.expected.reasonCode}`}
                                        </span>
                                        {stateChip(run)}
                                        <button
                                            className={styles.runButton}
                                            disabled={!basePayload || run?.state === 'running' || runningAll}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                runTest(test)
                                            }}
                                        >
                                            {run?.state === 'running' ? '…' : 'Run'}
                                        </button>
                                    </div>
                                    {isOpen && (
                                        <div className={styles.testDetail}>
                                            <p className={styles.description}>{test.description}</p>
                                            {run?.verdict && (
                                                <p
                                                    className={`${styles.verdict} ${
                                                        run.state === 'pass' ? styles.verdictPass : styles.verdictFail
                                                    }`}
                                                >
                                                    {run.verdict}
                                                </p>
                                            )}
                                            <div className={styles.panels}>
                                                <div className={styles.panel}>
                                                    <h5>Payload submitted</h5>
                                                    <pre>
                                                        {previewPayload
                                                            ? JSON.stringify(previewPayload, null, 2)
                                                            : 'Select an approved lender first.'}
                                                    </pre>
                                                </div>
                                                <div className={styles.panel}>
                                                    <h5>
                                                        API response
                                                        {run?.result &&
                                                            ` — HTTP ${run.result.http_status}${
                                                                run.result.rejection_reason
                                                                    ? ` · X-FFJ-Rejection-Reason: ${run.result.rejection_reason}`
                                                                    : ''
                                                            }`}
                                                    </h5>
                                                    <pre>
                                                        {run?.result
                                                            ? JSON.stringify(run.result.body, null, 2)
                                                            : 'Run the test to see the live response.'}
                                                    </pre>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )
            })}

            <div className={styles.categoryBlock}>
                <h4 className={styles.categoryTitle}>
                    Contact Fusion — Onboarding Webhook
                    <span className={styles.categoryCount}>live send</span>
                </h4>
                <p className={styles.note}>
                    When a FFJ client passes every gate and is <strong>100% onboarded</strong> (client, claims,
                    signature and all documents stored), the API fires an onboarding webhook to Contact Fusion with
                    the client&apos;s details, every claim (each with its document links) and the credit report URL.
                    The payload below is a fully mocked example of exactly what CF receives.{' '}
                    <strong>Send test webhook</strong> genuinely POSTs it to the CF endpoint so their side can be
                    wired up and verified — all values are unmistakably test data and it is flagged{' '}
                    <code>test: true</code>.
                </p>
                <div className={styles.cfControls}>
                    <div className={styles.cfUrl}>
                        <label>Webhook URL</label>
                        <code>{cfTest.webhookUrl ?? 'Loading…'}</code>
                    </div>
                    <button
                        className={styles.runAllButton}
                        onClick={sendCfTestWebhook}
                        disabled={!cfTest.payload || cfTest.sending}
                    >
                        {cfTest.sending ? 'Sending…' : 'Send test webhook to Contact Fusion'}
                    </button>
                </div>
                {cfTest.sendError && (
                    <p className={`${styles.verdict} ${styles.verdictFail}`}>{cfTest.sendError}</p>
                )}
                {cfTest.sendResult?.success && (
                    <p className={`${styles.verdict} ${styles.verdictPass}`}>
                        Contact Fusion accepted the test webhook (HTTP {cfTest.sendResult.cf_status}).
                    </p>
                )}
                <div className={styles.panels}>
                    <div className={styles.panel}>
                        <h5>Test payload (exact shape sent to Contact Fusion)</h5>
                        <pre>
                            {cfTest.payload
                                ? JSON.stringify(cfTest.payload, null, 2)
                                : 'Could not load the payload preview.'}
                        </pre>
                    </div>
                    <div className={styles.panel}>
                        <h5>
                            Contact Fusion response
                            {cfTest.sendResult?.cf_status != null && ` — HTTP ${cfTest.sendResult.cf_status}`}
                        </h5>
                        <pre>
                            {cfTest.sendResult
                                ? JSON.stringify(cfTest.sendResult.cf_response ?? cfTest.sendResult, null, 2)
                                : 'Send the test webhook to see Contact Fusion\u2019s live response.'}
                        </pre>
                    </div>
                </div>
            </div>
        </div>
    )
}
